import useSidePageWithTableCtrl from '@/shared/SidePage/useSidePageWithTableCtrl.ts'
import { useMemo, useState } from 'react'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import mdmApi from '@/pages/MDM/mdm.api.ts'
import mdmEvents from '@/pages/MDM/events/mdm.events.ts'
import MenuOptionEdit from '@/shared/Dropdowns/DropdownListItem/DropdownListItemEdit'
import MenuOptionDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import SearchInput from '@/shared/Inputs/SearchInput'
import MDMListGroup from '@/pages/MDM/components/MDMListGroup'
import MDMListValue from '@/pages/MDM/components/MDMListValue'
import useUnits from '@/pages/MDM/hooks/useUnits.ts'
import { IMDMUnit } from '@/pages/MDM/mdm.types.ts'
import { useRecoilValue } from 'recoil'
import mdmSelectors from '@/pages/MDM/mdm.selectors.ts'
import CreateUnitSidePage from '@/pages/MDM/components/Units/CreateUnitSidePage'
import UnitIcon from '@/assets/icons/mdm/aggregat-schema.svg?react'
import UnitInstanceIcon from '@/assets/icons/mdm/aggregat-instance.svg?react'
import UnitSidePage from '@/pages/MDM/components/Units/UnitSidePage'
import MenuOptionCreate from '@/shared/Dropdowns/DropdownListItem/DropdownListItemCreate'
import CreateUnitInstanceSidePage from '@/pages/MDM/components/Units/CreateUnitInstanceSidePage'
import useUnitInstances from '@/pages/MDM/hooks/useUnitInstances.ts'
import UnitInstanceSidePage from '@/pages/MDM/components/Units/UnitInstanceSidePage'
import Tooltip from 'src/shared/Common/Tooltip'
import ProtectedContent from '@/services/rights/ProtectedContent'
import useSelfAccessRightsSet from '@/services/rights/useSelfAccessRIghtsSet.ts'
import useDebounce from '@/shared/hooks/useDebounce.ts'

const UnitsTabContent = () => {
  const selfAccessRights = useSelfAccessRightsSet()

  const units = useUnits()
  const instances = useUnitInstances()

  const sidePageCtrl = useSidePageWithTableCtrl(units)
  const instancesSidePageCtrl = useSidePageWithTableCtrl(instances)

  const unitIdToInstancesMap = useRecoilValue(mdmSelectors.unitIdToInstancesMap)

  const [createInstanceUnitId, setCreateInstanceUnitId] = useState<IMDMUnit['id']>(null)
  const [createInstanceSidePageOpen, setCreateInstanceSidePageOpen] = useState(false)

  const [createSidePageOpen, setCreateSidePageOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const searchResults = useMemo(() => {
    if (!search) return units

    return units.filter(
      (p) =>
        p.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        unitIdToInstancesMap[p.id].some((v) => v.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
    )
  }, [units, unitIdToInstancesMap, debouncedSearch])

  const handleDeleteUnit = (unit: IMDMUnit) => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление агрегата ${unit.name}`,
      buttonType: 'primary',
      onConfirm: () =>
        mdmApi.deleteUnit(unit.id).then((r) => r.status === 'success' && mdmEvents.unitDeleted(r.body.id)),
    })
  }

  const renderMenuOptions = (e: IMDMUnit) => {
    if (
      !selfAccessRights.has('mdm:delete_schema') &&
      !selfAccessRights.has('mdm:update_schema') &&
      !selfAccessRights.has('mdm:create_schema')
    ) {
      return null
    }

    const unitHasInstances = Boolean(unitIdToInstancesMap[e.id]?.length)
    return (
      <div className="flex-lines">
        <MenuOptionCreate
          text="Добавить экземпляр"
          onClick={() => {
            setCreateInstanceUnitId(e.id)
            setCreateInstanceSidePageOpen(true)
          }}
        />
        <Tooltip
          text={'Агрегат содержит экземпляры.\nРедактирование недоступно'}
          active={unitHasInstances}
          position="left"
          style={{ width: '235px' }}
        >
          <MenuOptionEdit
            text="Редактировать"
            onClick={() => sidePageCtrl.handleItemPageOpen(e)}
            disabled={unitHasInstances}
          />
        </Tooltip>
        <MenuOptionDelete text="Удалить" onClick={() => handleDeleteUnit(e)} />
      </div>
    )
  }

  return (
    <div className="flex-lines gap24" style={{ minHeight: 0, height: '100%' }}>
      <ProtectedContent
        requiredRight="mdm:create_schema"
        fallBack={
          <span className="text-nm text-description">
            Для создания нового агрегата обратитесь к администратору системы
          </span>
        }
      >
        <CreateEntityButton onClick={() => setCreateSidePageOpen(true)} text="Создать агреграт" />
      </ProtectedContent>
      <SearchInput value={search} name="search" onChange={setSearch} placeHolder="Поиск по агрегатам" />
      <div className="flex-lines" style={{ overflow: 'auto', minHeight: 0, height: '100%' }}>
        {searchResults.map((p, index) => (
          <MDMListGroup
            renderSeparateLine={index !== units.length - 1}
            key={p.id}
            object={p as any}
            icon={<UnitIcon />}
            menuOptions={renderMenuOptions(p)}
          >
            {unitIdToInstancesMap[p.id].map((v) => (
              <MDMListValue
                key={v.id}
                name={v.name}
                icon={<UnitInstanceIcon />}
                onClick={() => instancesSidePageCtrl.handleItemPageOpen(v)}
              />
            ))}
          </MDMListGroup>
        ))}
      </div>
      <CreateUnitInstanceSidePage
        open={createInstanceSidePageOpen}
        unitId={createInstanceUnitId}
        onClose={() => setCreateInstanceSidePageOpen(false)}
      />
      <CreateUnitSidePage onClose={() => setCreateSidePageOpen(false)} open={createSidePageOpen} />
      <UnitSidePage
        onCloseAnimationEnd={sidePageCtrl.handleCloseAnimationEnd}
        open={sidePageCtrl.sidePageOpen}
        onClose={sidePageCtrl.handleClosePage}
        unit={sidePageCtrl.selectedItem}
      />
      <UnitInstanceSidePage
        onCloseAnimationEnd={instancesSidePageCtrl.handleCloseAnimationEnd}
        open={instancesSidePageCtrl.sidePageOpen}
        onClose={instancesSidePageCtrl.handleClosePage}
        instance={instancesSidePageCtrl.selectedItem}
      />
    </div>
  )
}

export default UnitsTabContent
